import { useRef, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import { X, AlertTriangle, Trash2 } from 'lucide-react'
import type { AdminUser } from '../../types'
import { useDialogA11y } from '../../hooks/useDialogA11y'

interface UserDeleteModalProps {
  user: AdminUser
  onClose: () => void
  onSuccess: () => void
}

export default function UserDeleteModal({ user, onClose, onSuccess }: UserDeleteModalProps) {
  const { deleteUser, usersLoading } = useAdminStore()
  const [typedName, setTypedName] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  useDialogA11y(true, dialogRef, onClose)

  const fullName = `${user.firstName} ${user.lastName}`.trim()
  const namesMatch = typedName.trim() === fullName

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!namesMatch) {
      setError('השם שהוזן אינו תואם את שם המשתמש')
      return
    }

    try {
      await deleteUser(user._id, {
        confirmedName: typedName.trim(),
        reason: reason.trim() || undefined,
      })
      onSuccess()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'שגיאה במחיקת המשתמש')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-delete-modal-title"
        className="bg-white rounded-lg max-w-md w-full"
      >
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 id="user-delete-modal-title" className="text-xl font-heading font-bold text-red-700 flex items-center gap-2">
            <Trash2 size={22} aria-hidden="true" />
            מחיקת משתמש
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="סגירה">
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Danger banner */}
          <div className="alert alert-error">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <p className="font-semibold">פעולה זו אינה הפיכה</p>
              <p className="text-sm mt-1">
                כל נתוני המשתמש ימחקו לצמיתות (שאלונים, שיחות, המלצות, פידבק, אישורי סיכון).
                יומני הביקורת יישמרו למטרות רגולציה.
              </p>
            </div>
          </div>

          {/* User being deleted */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">משתמש למחיקה</p>
            <p className="font-medium text-lg">{fullName}</p>
            <p className="text-sm text-gray-600 font-mono" dir="ltr">{user.email}</p>
          </div>

          {/* Name confirmation */}
          <div>
            <label htmlFor="user-delete-confirm-name" className="block text-sm font-medium text-gray-700 mb-2">
              לאישור המחיקה, הקלד/י את שם המשתמש המלא:{' '}
              <span className="font-semibold text-gray-900">{fullName}</span>
            </label>
            <input
              id="user-delete-confirm-name"
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={fullName}
              className={`input ${
                typedName && !namesMatch ? 'input-error' : ''
              }`}
              autoComplete="off"
            />
            {typedName && !namesMatch && (
              <p role="alert" className="text-sm text-red-600 mt-1">השם אינו תואם</p>
            )}
          </div>

          {/* Optional reason */}
          <div>
            <label htmlFor="user-delete-reason" className="block text-sm font-medium text-gray-700 mb-2">
              סיבת המחיקה (אופציונלי)
            </label>
            <textarea
              id="user-delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="תיעוד לצורך ביקורת..."
              className="input"
            />
          </div>

          {error && (
            <div className="alert alert-error" role="alert">
              <p>{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={usersLoading}
            >
              ביטול
            </button>
            <button
              type="submit"
              className="btn btn-error"
              disabled={usersLoading || !namesMatch}
            >
              {usersLoading ? 'מוחק...' : 'מחק משתמש לצמיתות'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
